// v2 ore-rock — a stratified ore body: a stepped stack of angular slate-grey
// slabs breaking out of the ground at a tilt, with rust-iron seams clamped
// between the strata and metallic nodes studding the exposed faces, ringed by
// dark scree. Each variant re-tilts the strata and re-seats the nodes. Ported
// 1:1 from age-of-mcp's v2/ore-rock.ts, using the library's shared `mineral`
// (h01, lerp, scree) helpers.
import * as THREE from "three";
import { defineModel, mineral, type Part } from "../../lib";

const HOST  = "#565a63";   // dark slate host rock
const HOST2 = "#62666f";
const SEAM  = "#8a4f33";   // rust-iron seam between strata
const NODE  = "#3d424a";   // near-black metallic node
const GLINT = "#9aa3ad";   // cold metallic glint facet

function oreRockParts(v: number): Part[] {
  const seed = v * 11 + 3;
  const yaw  = v * 1.9;
  const tilt = mineral.lerp(0.08, 0.18, mineral.h01(seed, 0));
  const parts: Part[] = [];

  // Three stepped strata slabs, each narrower and turned a little further,
  // with a thin rust seam squeezed between each pair.
  const widths  = [0.5, 0.4, 0.3];
  const heights = [0.16, 0.14, 0.12];
  let y = 0;
  for (let i = 0; i < 3; i++) {
    const w = widths[i]!, hh = heights[i]!;
    parts.push({
      geometry: new THREE.BoxGeometry(w, hh, w * 0.82),
      color: i % 2 === 0 ? HOST : HOST2,
      position: [mineral.lerp(-0.04, 0.04, mineral.h01(seed, i * 3 + 1)), y + hh / 2, 0],
      rotation: [tilt * (i === 1 ? -0.6 : 1), yaw + i * 0.5, tilt * 0.4],
    });
    if (i < 2) {
      parts.push({
        geometry: new THREE.BoxGeometry(w * 0.92, 0.028, w * 0.76),
        color: SEAM,
        position: [0, y + hh + 0.004, 0],
        rotation: [tilt * (i === 0 ? 1 : -0.6), yaw + i * 0.5 + 0.25, tilt * 0.4],
      });
    }
    y += hh - 0.015;                       // strata sit pressed into each other
  }

  // Metallic ore nodes studding the exposed faces — dark lumps with one
  // brighter glint shard so the metal catches the key light.
  for (let i = 0; i < 4; i++) {
    const ang = yaw + i * 1.7 + mineral.h01(seed, 20 + i) * 0.8;
    const d = mineral.lerp(0.16, 0.24, mineral.h01(seed, 30 + i));
    const r = mineral.lerp(0.05, 0.085, mineral.h01(seed, 40 + i));
    parts.push({
      geometry: new THREE.IcosahedronGeometry(r, 0),
      color: i === 1 ? GLINT : NODE,
      position: [Math.cos(ang) * d, mineral.lerp(0.1, 0.34, mineral.h01(seed, 50 + i)), Math.sin(ang) * d],
      rotation: [0, ang, mineral.h01(seed, 60 + i) * 0.7],
    });
  }

  parts.push(...mineral.scree(seed, 4, [0.3, 0.4], [0.035, 0.06], [HOST, NODE, HOST2]));
  return parts;
}

export const oreRockV2 = defineModel({
  id: "ore-rock-v2",
  batchId: "fable",
  name: "Ore rock (v2)",
  tags: ["scenery","ore","rock","low-poly","age-of-mcp","fable"],
  summary: "Low-poly ore rock.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.46,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => oreRockParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => oreRockParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => oreRockParts(2) },
  ],
  animations: [],
});
